import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X, Calendar, MapPin } from 'lucide-react';
import { useModalScrollLock } from '@/hooks/useModalScrollLock';
import { useOrganizationPublicEvents } from '@/hooks/useOrganizationPublicEvents';
import type { MarketplaceOrganization } from '@/types/marketplace';
import type { Organization } from '@/types/events';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import { EventImage } from '@/components/common/EventImage';
import { formatDateRange } from '@/utils/dateFormat';

const ROW_MIN_H = '5.5rem';
const DESCRIPTION_WORD_LIMIT = 40;

function orgDisplayName(o: MarketplaceOrganization): string {
  const n = [o.first_name, o.last_name].filter(Boolean).join(' ').trim();
  return n || 'Organization';
}

interface MarketplaceOrganizationProfileModalProps {
  organization: MarketplaceOrganization | null;
  onClose: () => void;
  onEventPress: (event: Organization) => void;
  volunteerUserId: string | null;
}

export function MarketplaceOrganizationProfileModal({
  organization,
  onClose,
  onEventPress,
  volunteerUserId,
}: MarketplaceOrganizationProfileModalProps) {
  const visible = organization != null;
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  // When expanding, freeze the panel at its current height so it scrolls
  // internally instead of growing. Null = size to content.
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  useModalScrollLock(visible);

  // Collapse the description again whenever a different organization is opened.
  useEffect(() => {
    setDescriptionExpanded(false);
    setLockedHeight(null);
  }, [organization?.id]);

  const toggleDescription = () => {
    setDescriptionExpanded((expanded) => {
      if (expanded) {
        setLockedHeight(null);
        return false;
      }
      setLockedHeight(panelRef.current?.offsetHeight ?? null);
      return true;
    });
  };

  const { events, loading, error } = useOrganizationPublicEvents(
    organization?.id ?? null,
    volunteerUserId,
    organization?.membershipStatus === 'accepted'
  );

  if (!visible || !organization) return null;

  const name = orgDisplayName(organization);

  return createPortal(
    <Flex
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.45)"
      align="center"
      justify="center"
      zIndex={10000}
      px="4"
      py="5"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Box
        ref={panelRef}
        bg="white"
        borderRadius="24px"
        w="full"
        maxW="440px"
        h={lockedHeight != null ? `${lockedHeight}px` : undefined}
        maxH="90vh"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        boxShadow="0 20px 40px rgba(0,0,0,0.15)"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          align="center"
          justify="space-between"
          px="5"
          py="4"
          borderBottom="1px solid"
          borderColor="volyx.mediumGray"
          flexShrink={0}
        >
          <Text fontSize="lg" fontWeight="bold" color="volyx.text" lineClamp={2} pr="2">
            {name}
          </Text>
          <chakra.button
            type="button"
            aria-label="Close"
            onClick={onClose}
            borderRadius="md"
            p="1"
            flexShrink={0}
            _hover={{ bg: 'volyx.lightGray' }}
          >
            <X size={22} color="#1F2932" />
          </chakra.button>
        </Flex>

        <Box px="5" py="4" overflowY="auto" flex="1">
          <Flex align="flex-start" gap="4" mb="5">
            <Box
              w="72px"
              h="72px"
              borderRadius="xl"
              overflow="hidden"
              flexShrink={0}
              bg="volyx.lightGray"
            >
              <chakra.img
                src={organization.profile_image || DEFAULT_PROFILE_IMAGE}
                alt=""
                w="full"
                h="full"
                objectFit="cover"
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const t = e.target as HTMLImageElement;
                  if (t.src !== DEFAULT_PROFILE_IMAGE) t.src = DEFAULT_PROFILE_IMAGE;
                }}
              />
            </Box>
            <Box minW="0" flex="1">
              {organization.location ? (
                <Flex align="center" gap="1.5" mb="2" color="volyx.textSecondary">
                  <MapPin size={14} aria-hidden />
                  <Text fontSize="sm" lineClamp={2}>
                    {organization.location}
                  </Text>
                </Flex>
              ) : null}
              {organization.description ? (
                (() => {
                  const words = organization.description.trim().split(/\s+/);
                  const isLong = words.length > DESCRIPTION_WORD_LIMIT;
                  const shown =
                    isLong && !descriptionExpanded
                      ? `${words.slice(0, DESCRIPTION_WORD_LIMIT).join(' ')}…`
                      : organization.description;
                  return (
                    <Text fontSize="sm" color="volyx.text" whiteSpace="pre-wrap">
                      {shown}
                      {isLong ? (
                        <chakra.button
                          type="button"
                          onClick={toggleDescription}
                          ml="1"
                          color="rgba(0, 122, 255, 1)"
                          fontWeight="semibold"
                          _hover={{ textDecoration: 'underline' }}
                        >
                          {descriptionExpanded ? 'Less' : 'More'}
                        </chakra.button>
                      ) : null}
                    </Text>
                  );
                })()
              ) : (
                <Text fontSize="sm" color="volyx.textSecondary">
                  No description.
                </Text>
              )}
            </Box>
          </Flex>

          <Text fontSize="sm" fontWeight="semibold" color="volyx.text" mb="3">
            Public events
          </Text>

          {loading ? (
            <Text fontSize="sm" color="volyx.textSecondary">
              Loading events…
            </Text>
          ) : error ? (
            <Text fontSize="sm" color="red.500">
              {error}
            </Text>
          ) : events.length === 0 ? (
            <Text fontSize="sm" color="volyx.textSecondary">
              No public events yet.
            </Text>
          ) : (
            <Flex direction="column" gap="3">
              {events.map((event) => {
                const dateLabel = formatDateRange(event.start_date ?? null, event.end_date ?? null);
                const metaLine = [dateLabel, event.location].filter(Boolean).join(' · ');
                return (
                  <chakra.button
                    key={event.id}
                    type="button"
                    display="flex"
                    alignItems="center"
                    gap="3"
                    textAlign="left"
                    w="full"
                    bg="white"
                    borderRadius="24px"
                    border="1px solid"
                    borderColor="volyx.mediumGray"
                    px="4"
                    py="3"
                    minH={ROW_MIN_H}
                    lineHeight="1.25"
                    boxShadow="0 2px 10px rgba(0,0,0,0.06)"
                    cursor="pointer"
                    transition="box-shadow 0.2s"
                    _hover={{ boxShadow: '0 6px 18px rgba(0,0,0,0.12)' }}
                    onClick={() => {
                      onClose();
                      onEventPress(event);
                    }}
                  >
                    <Box
                      w="56px"
                      h="56px"
                      borderRadius="xl"
                      overflow="hidden"
                      flexShrink={0}
                      bg="volyx.lightGray"
                    >
                      <EventImage
                        stored={event.image}
                        w="full"
                        h="full"
                        objectFit="cover"
                      />
                    </Box>
                    <Box minW="0" flex="1">
                      <Flex align="center" gap="1.5" mb="0.5">
                        <Calendar size={14} color="#6B7280" aria-hidden />
                        <Text fontWeight="semibold" fontSize="sm" color="volyx.text" lineClamp={1}>
                          {event.name}
                        </Text>
                      </Flex>
                      {metaLine ? (
                        <Text fontSize="xs" color="volyx.textSecondary" lineClamp={1}>
                          {metaLine}
                        </Text>
                      ) : null}
                    </Box>
                    <Box
                      as="span"
                      flexShrink={0}
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      px="3"
                      py="2"
                      borderRadius="xl"
                      fontSize="xs"
                      fontWeight="semibold"
                      lineHeight="1.2"
                      bg="rgba(0, 122, 255, 1)"
                      color="white"
                      pointerEvents="none"
                    >
                      View
                    </Box>
                  </chakra.button>
                );
              })}
            </Flex>
          )}
        </Box>
      </Box>
    </Flex>,
    document.body
  );
}
